
import React, { useState } from 'react';
import {
  CheckCircle2,
  Bus,
  Users,
  Calendar,
  MapPin,
  User,
  Bell,
  AlertTriangle,
  Loader2,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { format } from 'date-fns';

const Step9Summary = ({ wizardData, onComplete }) => {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [activating, setActivating] = useState(false);

  const handleActivate = async () => {
    setActivating(true);
    
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 2000));
    
    setActivating(false);
    setShowConfirmDialog(false);
    
    // Show success and complete
    onComplete();
  };

  const summaryItems = [
    {
      icon: Bus,
      title: 'Merge Type',
      value: wizardData.mergeType === 'instant' ? 'Instant Merge' : 'Scheduled Merge',
      description: wizardData.mergeType === 'instant' 
        ? 'Immediate emergency merge' 
        : 'Planned merge for specific date',
    },
    {
      icon: Bus,
      title: 'Destination Bus',
      value: wizardData.destinationBus?.busNumber,
      description: `Driver: ${wizardData.destinationBus?.driver} • Capacity: ${wizardData.destinationBus?.capacity} students`,
    },
    {
      icon: Bus,
      title: 'Source Buses',
      value: `${wizardData.sourceBuses?.length || 0} buses`,
      description: wizardData.sourceBuses?.map((b) => b.busNumber).join(', '),
    },
  ];

  if (wizardData.mergeType === 'scheduled') {
    summaryItems.push({
      icon: Calendar,
      title: 'Schedule',
      value: wizardData.mergeDate ? format(wizardData.mergeDate, 'MMMM d, yyyy') : 'Not set',
      description: wizardData.shift ? `${wizardData.shift.name} (${wizardData.shift.type})` : 'No shift selected',
    });
  }

  summaryItems.push(
    {
      icon: Users,
      title: 'Students',
      value: `${wizardData.selectedStudents?.length || 0} students`,
      description: `Transferring to ${wizardData.destinationBus?.busNumber}`,
    },
    {
      icon: MapPin,
      title: 'Route Stops',
      value: `${wizardData.routeOrder?.length || 0} stops`,
      description: 'Route order configured',
    },
    {
      icon: User,
      title: 'Driver',
      value: wizardData.useDefaultDriver 
        ? wizardData.destinationBus?.driver 
        : wizardData.driver?.name,
      description: wizardData.useDefaultDriver ? 'Using default driver' : 'Custom driver assigned',
    },
    {
      icon: Bell,
      title: 'Notifications',
      value: `${wizardData.notification?.recipients?.length || 0} recipients`,
      description: `Timing: ${wizardData.notification?.timing === 'immediate' ? 'Immediate' : wizardData.notification?.timing}`,
    }
  );

  const totalStudents = wizardData.selectedStudents?.length || 0;
  const destinationCapacity = wizardData.destinationBus?.capacity || 0;
  const isOverCapacity = totalStudents > destinationCapacity;

  return (
    <div className="space-y-6">
      <div>
        <p className="text-gray-600 mb-2">
          Review all the details below before activating the merge session. Once activated, the
          merge will take effect and notifications will be sent.
        </p>
      </div>

      {/* Warning Banner */}
      {isOverCapacity && (
        <Card className="p-4 bg-red-50 border-red-200">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
            <div>
              <h4 className="font-semibold text-red-900 mb-1">Capacity Warning</h4>
              <p className="text-sm text-red-800">
                Total students ({totalStudents}) exceeds destination bus capacity ({destinationCapacity}).
                Please review and adjust before proceeding.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Summary Grid */}
      <div className="grid md:grid-cols-2 gap-4">
        {summaryItems.map((item, index) => {
          const Icon = item.icon;
          return (
            <Card key={index} className="p-5">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
                  <Icon className="h-5 w-5 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-medium text-gray-500 mb-1">{item.title}</h3>
                  <p className="text-lg font-semibold text-gray-900 mb-1 truncate">{item.value}</p>
                  <p className="text-sm text-gray-600 line-clamp-2">{item.description}</p>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Detailed Summary Card */}
      <Card className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <CheckCircle2 className="h-5 w-5 text-blue-600" />
          Merge Session Summary
        </h3>
        <div className="space-y-3 text-sm">
          <div className="flex justify-between py-2 border-b border-blue-200">
            <span className="text-gray-700">Merge Type:</span>
            <span className="font-semibold text-gray-900">
              {wizardData.mergeType === 'instant' ? 'Instant Merge' : 'Scheduled Merge'}
            </span>
          </div>
          <div className="flex justify-between py-2 border-b border-blue-200">
            <span className="text-gray-700">Destination Bus:</span>
            <span className="font-semibold text-gray-900">{wizardData.destinationBus?.busNumber}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-blue-200">
            <span className="text-gray-700">Source Buses:</span>
            <span className="font-semibold text-gray-900">{wizardData.sourceBuses?.length}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-blue-200">
            <span className="text-gray-700">Total Students:</span>
            <span className={`font-semibold ${isOverCapacity ? 'text-red-600' : 'text-gray-900'}`}>
              {totalStudents} / {destinationCapacity}
            </span>
          </div>
          <div className="flex justify-between py-2 border-b border-blue-200">
            <span className="text-gray-700">Route Stops:</span>
            <span className="font-semibold text-gray-900">{wizardData.routeOrder?.length}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-blue-200">
            <span className="text-gray-700">Driver:</span>
            <span className="font-semibold text-gray-900">
              {wizardData.useDefaultDriver 
                ? wizardData.destinationBus?.driver 
                : wizardData.driver?.name}
            </span>
          </div>
          <div className="flex justify-between py-2 border-b border-blue-200">
            <span className="text-gray-700">Notifications:</span>
            <span className="font-semibold text-gray-900">
              {wizardData.notification?.recipients?.length} recipients
            </span>
          </div>
          {wizardData.mergeType === 'scheduled' && (
            <div className="flex justify-between py-2">
              <span className="text-gray-700">Scheduled For:</span>
              <span className="font-semibold text-gray-900">
                {wizardData.mergeDate && format(wizardData.mergeDate, 'MMM d, yyyy')} - {wizardData.shift?.name}
              </span>
            </div>
          )}
        </div>
      </Card>

      {/* Action Button */}
      <div className="flex justify-center pt-4">
        <Button
          onClick={() => setShowConfirmDialog(true)}
          disabled={isOverCapacity}
          size="lg"
          className="bg-green-600 hover:bg-green-700 text-white px-8 h-12 text-base font-semibold"
        >
          <CheckCircle2 className="h-5 w-5 mr-2" />
          Activate Merge Session
        </Button>
      </div>

      {isOverCapacity && (
        <p className="text-center text-sm text-red-600">
          Cannot activate merge session due to capacity issues. Please go back and adjust.
        </p>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-600" />
              Confirm Merge Activation
            </DialogTitle>
            <DialogDescription className="space-y-3 pt-2">
              <p>
                Are you sure you want to activate this merge session? This action will:
              </p>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>Combine {wizardData.sourceBuses?.length} buses into {wizardData.destinationBus?.busNumber}</li>
                <li>Transfer {totalStudents} students to the new route</li>
                <li>Send notifications to {wizardData.notification?.recipients?.length} recipients</li>
                {wizardData.mergeType === 'instant' && (
                  <li className="font-semibold text-yellow-700">Take effect immediately</li>
                )}
              </ul>
              <p className="font-semibold text-gray-900 pt-2">
                This action cannot be easily undone.
              </p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowConfirmDialog(false)}
              disabled={activating}
            >
              Cancel
            </Button>
            <Button
              onClick={handleActivate}
              disabled={activating}
              className="bg-green-600 hover:bg-green-700"
            >
              {activating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Activating...
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Yes, Activate
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Step9Summary;
